import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios: always send cookies
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" · ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/**
 * Silent, single-flight refresh: on any 401 (except on /auth endpoints
 * that must not loop), transparently call `/auth/refresh` and retry the
 * original request once. This preserves the user's session across:
 *   • access_token expiry (15 min);
 *   • browser/PWA close & reopen (refresh_token cookie lives 30 gg,
 *     rotated on each refresh);
 * without ever exposing tokens or credentials to JS/localStorage
 * (cookies remain httpOnly + Secure + SameSite=None).
 */
let refreshInFlight = null;
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;
    const url = originalRequest.url || "";
    const isAuthCriticalEndpoint =
      url.includes("/auth/refresh") ||
      url.includes("/auth/login") ||
      url.includes("/auth/register");
    if (
      status === 401 &&
      !originalRequest._retry &&
      !isAuthCriticalEndpoint
    ) {
      originalRequest._retry = true;
      try {
        if (!refreshInFlight) {
          refreshInFlight = axios
            .post(`${API}/auth/refresh`)
            .finally(() => {
              refreshInFlight = null;
            });
        }
        await refreshInFlight;
        return axios(originalRequest);
      } catch (refreshErr) {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  // null = still checking; false = anonymous; object = authenticated user
  const [user, setUser] = useState(null);
  // NDA state fetched together with /me. null = unknown / anonymous.
  const [ndaState, setNdaState] = useState(null);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data.user);
      setNdaState(data.nda || null);
      return data.user;
    } catch {
      setUser(false);
      setNdaState(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data.user);
    // After login, immediately fetch NDA state so <NdaGate/> can react
    // without waiting for the next /me poll.
    try {
      const meResp = await axios.get(`${API}/auth/me`);
      setNdaState(meResp.data?.nda || null);
    } catch {
      /* ignore — gate will remain until next refresh */
    }
    return data.user;
  }, []);

  const register = useCallback(async (email, password, name, extras = {}) => {
    const { data } = await axios.post(`${API}/auth/register`, {
      email,
      password,
      name,
      terms_accepted: extras.terms_accepted === true,
      terms_version: extras.terms_version || undefined,
    });
    // Do NOT auto-login: user must confirm email first.
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      /* ignore */
    }
    setUser(false);
    setNdaState(null);
  }, []);

  const changePassword = useCallback(async (current_password, new_password) => {
    const { data } = await axios.post(`${API}/auth/change-password`, {
      current_password,
      new_password,
    });
    return data;
  }, []);

  const updateProfile = useCallback(async (name) => {
    const { data } = await axios.patch(`${API}/auth/profile`, { name });
    setUser(data.user);
    return data.user;
  }, []);

  const forgotPassword = useCallback(async (email) => {
    const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
    return data;
  }, []);

  const resetPassword = useCallback(async (token, new_password) => {
    const { data } = await axios.post(`${API}/auth/reset-password`, {
      token,
      new_password,
    });
    return data;
  }, []);

  const verifyEmail = useCallback(async (token) => {
    const { data } = await axios.post(`${API}/auth/verify-email`, { token });
    return data;
  }, []);

  const resendVerification = useCallback(async (email) => {
    const { data } = await axios.post(`${API}/auth/resend-verification`, {
      email,
    });
    return data;
  }, []);

  const value = {
    user,
    ndaState,
    isAuthenticated: !!user && typeof user === "object",
    isLoading: user === null,
    login,
    register,
    logout,
    changePassword,
    updateProfile,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerification,
    refreshMe,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
